import React, { useEffect, useState } from "react";
import type {
    AccountData,
    BillingData,
    Invoice,
    LedgerTransaction,
    UsageData,
} from "../../../hooks/useMyAccountData";
import {
    AgreementDocument,
    getSignedAgreements,
    getSignedDocUrl,
} from "../../../services/agreementService";
import {
    getWarningClassName,
    getWarningIcon,
} from "../../../services/usageWarningService";
import type { ActiveUsageWarning } from "../../../types/billing.types";
import { formatDateShort } from "../../../utils/dateFormatters";
import { formatSectorLabel } from "../../../utils/sectorLabels";
import "../../billing/UsageWarningBanner.css";
import "../MyAccount.css";

import CreditHistoryModal from "./CreditHistoryModal";
import EditAccountModal from "./EditAccountModal";
import EditAdminModal from "./EditAdminModal";
import InvoicesModal from "./InvoicesModal";
import PaymentMethodModal from "./PaymentMethodModal";
import TopUpModal from "./TopUpModal";

interface MyAccountUIProps {
  accountData: AccountData;
  billingData: BillingData;
  usageData: UsageData;
  invoices: Invoice[];
  ledger: LedgerTransaction[];
  warnings: ActiveUsageWarning[];
  showChangePlanModal: boolean;
  setShowChangePlanModal: (show: boolean) => void;
  showPaymentModal: boolean;
  setShowPaymentModal: (show: boolean) => void;
  showInvoicesModal: boolean;
  setShowInvoicesModal: (show: boolean) => void;
  openInvoicesModal?: () => void;
  invoicesLoading?: boolean;
  showEditAccountModal: boolean;
  setShowEditAccountModal: (show: boolean) => void;
  showEditAdminModal: boolean;
  setShowEditAdminModal: (show: boolean) => void;
  selectedPlan: string;
  setSelectedPlan: (plan: string) => void;
  editAccountData: AccountData;
  setEditAccountData: (data: AccountData) => void;
  handleChangePlan: () => void;
  handlePaymentSuccess?: (creditsAdded: number) => void;
  handleUpdatePayment: () => void;
  handleEditAccount: () => void;
  handleEditAdmin: () => void;
}

export const MyAccountUI: React.FC<MyAccountUIProps> = ({
  accountData,
  billingData,
  usageData,
  invoices,
  ledger,
  warnings,
  showChangePlanModal,
  setShowChangePlanModal,
  showPaymentModal,
  setShowPaymentModal,
  showInvoicesModal,
  setShowInvoicesModal,
  openInvoicesModal,
  invoicesLoading,
  showEditAccountModal,
  setShowEditAccountModal,
  showEditAdminModal,
  setShowEditAdminModal,
  selectedPlan,
  setSelectedPlan,
  editAccountData,
  setEditAccountData,
  handleChangePlan,
  handlePaymentSuccess,
  handleUpdatePayment,
  handleEditAccount,
  handleEditAdmin,
}) => {
  const [showPaymentStep, setShowPaymentStep] = useState(false);
  const [copiedId, setCopiedId] = useState(false);
  const [signedAgreements, setSignedAgreements] = useState<AgreementDocument[]>(
    [],
  );
  const [showLedgerModal, setShowLedgerModal] = useState(false);

  useEffect(() => {
    if (!accountData.accountId) return;
    let cancelled = false;
    getSignedAgreements(accountData.accountId)
      .then((docs) => {
        if (!cancelled) setSignedAgreements(docs);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [accountData.accountId]);

  return (
    <div className="portal-content">
      <div className="content-header">
        <div className="relative pl-5">
          <div className="absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b from-indigo-500 to-purple-500 rounded-full" />
          <h1
            className="text-3xl font-black tracking-tight mb-1"
            style={{
              background:
                "linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #a855f7 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            My Account
          </h1>
          <p className="text-gray-500 text-sm font-medium">
            Manage your account, billing, and usage
          </p>
        </div>
      </div>

      {/* ── Row 1: Account Details + Admin Contact ── */}
      <div className="account-row-2col">
        {/* Account Details Card */}
        <div className="account-card" data-testid="account-details-card">
          <div className="card-header blue">
            <div className="card-header-left">
              <span className="card-icon blue">🏢</span>
              <h2>Account Details</h2>
            </div>
            <button
              className="action-button secondary"
              data-testid="edit-account-details-button"
              onClick={() => setShowEditAccountModal(true)}
            >
              Edit
            </button>
          </div>
          <div className="card-content">
            <div className="detail-row">
              <span className="label">Organisation</span>
              <span className="value" data-testid="organisation-value">
                {accountData.organisation}
              </span>
            </div>
            <div className="detail-row">
              <span className="label">ABN</span>
              <span className="value mono" data-testid="abn-value">
                {accountData.abn}
              </span>
            </div>
            {accountData.acn && (
              <div className="detail-row">
                <span className="label">ACN</span>
                <span className="value mono" data-testid="acn-value">
                  {accountData.acn}
                </span>
              </div>
            )}
            <div className="detail-row">
              <span className="label">Account ID</span>
              <span
                className={`value account-id-chip${copiedId ? " copied" : ""}`}
                data-testid="account-id-value"
                title="Click to copy"
                onClick={() => {
                  navigator.clipboard.writeText(accountData.accountId);
                  setCopiedId(true);
                  setTimeout(() => setCopiedId(false), 1500);
                }}
              >
                {accountData.accountId || "-"}
                <span className="copy-btn">
                  {copiedId ? "✓ Copied" : "Copy"}
                </span>
              </span>
            </div>
            {accountData.reportingEntityType && (
              <div className="detail-row">
                <span className="label">Reporting Entity</span>
                <span className="value" data-testid="reporting-entity-type-value">
                  {formatSectorLabel(accountData.reportingEntityType)}
                </span>
              </div>
            )}
            {accountData.services && (
              <div className="detail-row">
                <span className="label">Services</span>
                <span className="value" data-testid="services-value">
                  {formatSectorLabel(accountData.services)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Admin Contact Card */}
        <div className="account-card" data-testid="admin-contact-card">
          <div className="card-header purple">
            <div className="card-header-left">
              <span className="card-icon purple">👤</span>
              <h2>Admin Contact</h2>
            </div>
            <button
              className="action-button secondary"
              data-testid="edit-admin-contact-button"
              onClick={() => setShowEditAdminModal(true)}
            >
              Edit
            </button>
          </div>
          <div className="card-content">
            <div className="detail-row">
              <span className="label">Name</span>
              <span className="value" data-testid="admin-name-value">
                {accountData.adminName || "-"}
              </span>
            </div>
            <div className="detail-row">
              <span className="label">Email</span>
              <span className="value" data-testid="admin-email-value">
                {accountData.adminEmail || "-"}
              </span>
            </div>
            <div className="detail-row">
              <span className="label">Phone</span>
              <span className="value" data-testid="admin-phone-value">
                {accountData.adminPhone || "-"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Row 2: Documents ── */}
      <div className="account-grid">
        <div className="account-card" data-testid="documents-card">
          <div className="card-header slate">
            <div className="card-header-left">
              <span className="card-icon slate">📄</span>
              <h2>Documents</h2>
            </div>
          </div>
          <div className="card-content">
            <div className="documents-list">
              {signedAgreements.length > 0 ? (
                signedAgreements.map((agreement) => (
                  <div
                    key={agreement.id}
                    className="document-item"
                    data-testid={`document-item-${agreement.id}`}
                  >
                    <div className="document-item-icon">📄</div>
                    <div className="document-item-body">
                      <div className="document-item-title">
                        {agreement.fileName || "ARCaml Services Agreement"}
                      </div>
                      <div className="document-item-meta">
                        Signed by {agreement.signedBy} on{" "}
                        {formatDateShort(agreement.signedAt)} &middot; v
                        {agreement.agreementVersion}
                      </div>
                    </div>
                    <div className="document-item-actions">
                      <span className="doc-status-badge signed">✔ Signed</span>
                      <button
                        className="doc-view-btn"
                        data-testid="signed-agreement-view-link"
                        onClick={async () => {
                          const url =
                            agreement.downloadUrl ||
                            (await getSignedDocUrl(agreement.fileName));
                          if (url)
                            window.open(url, "_blank", "noopener,noreferrer");
                        }}
                      >
                        View
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div
                  className="document-item-empty"
                  data-testid="documents-empty"
                >
                  No signed documents on file.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Row 3: Plan & Billing + Payment Method ── */}
      <div className="account-row-2col">
        <div className="account-card" data-testid="plan-billing-card">
          <div className="card-header green">
            <div className="card-header-left">
              <span className="card-icon green">💳</span>
              <h2>Plan & Billing</h2>
            </div>
            <button
              className="action-button primary"
              onClick={() => setShowChangePlanModal(true)}
              data-testid="top-up-credits-button"
            >
              Top Up Credits
            </button>
          </div>
          <div className="card-content">
            <div className="detail-row" data-testid="payment-method-row">
              <span className="label">Payment Method</span>
              <span className="value" data-testid="payment-method-value">
                {billingData.paymentMethod === "card"
                  ? "Credit Card"
                  : billingData.paymentMethod === "bank"
                    ? "Bank Transfer"
                    : billingData.paymentMethod === "applepay"
                      ? "Apple Pay"
                      : "Google Pay"}
              </span>
            </div>
            <div className="detail-row" data-testid="billing-email-row">
              <span className="label">Billing Email</span>
              <span className="value" data-testid="billing-email-value">
                {billingData.stripeBillingEmail || accountData.adminEmail || "-"}
              </span>
            </div>
            <div className="detail-row" data-testid="start-date-row">
              <span className="label">Start Date</span>
              <span className="value" data-testid="start-date-value">
                {billingData.startDate
                  ? formatDateShort(billingData.startDate)
                  : "-"}
              </span>
            </div>
          </div>
        </div>

        <div className="account-card" data-testid="payment-method-card">
          <div className="card-header amber">
            <div className="card-header-left">
              <span className="card-icon amber">🔒</span>
              <h2>Payment Method</h2>
            </div>
            <button
              className="action-button primary"
              data-testid="update-payment-button"
              onClick={() => setShowPaymentModal(true)}
            >
              Update
            </button>
          </div>
          <div className="card-content">
            <div className="detail-row">
              <span className="label">Method</span>
              <span className="value" data-testid="payment-method-type-value">
                {billingData.paymentMethod === "card"
                  ? "💳 Credit / Debit Card"
                  : billingData.paymentMethod === "bank"
                    ? "🏦 Bank Transfer"
                    : billingData.paymentMethod === "applepay"
                      ? " Apple Pay"
                      : billingData.paymentMethod === "googlepay"
                        ? "G Google Pay"
                        : billingData.paymentMethod}
              </span>
            </div>
            <button
              className="action-button secondary"
              data-testid="view-invoices-button"
              style={{
                marginTop: "16px",
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
              }}
              onClick={() => (openInvoicesModal ? openInvoicesModal() : setShowInvoicesModal(true))}
            >
              View Invoices
            </button>
            <button
              className="action-button secondary"
              data-testid="view-credit-history-button"
              style={{
                marginTop: "8px",
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
              }}
              onClick={() => setShowLedgerModal(true)}
            >
              📊 Credit History
            </button>
          </div>
        </div>
      </div>

      {/* ── Row 4: Credits & Usage + Verification Status ── */}
      <div className="account-row-2col">
        <div className="account-card" data-testid="credits-usage-card">
          <div className="card-header teal">
            <div className="card-header-left">
              <span className="card-icon teal">📊</span>
              <h2>Credits & Usage</h2>
            </div>
          </div>
          <div className="card-content">
            {warnings.length > 0 && (
              <div
                className={`${getWarningClassName(warnings[0].level)} usage-warning-non-dismissable`}
                data-testid="usage-warning-banner"
              >
                <div className="warning-content">
                  <span className="warning-icon" aria-hidden="true">
                    {getWarningIcon(warnings[0].level)}
                  </span>
                  <div className="warning-text">
                    <p
                      className="warning-message"
                      data-testid="warning-message"
                    >
                      {warnings[0].message}
                    </p>
                  </div>
                </div>
              </div>
            )}
            <div className="metric-item" data-testid="monthly-credits-metric">
              <span className="metric-label">Credits Used</span>
              <div className="progress-bar" data-testid="credits-progress-bar">
                <div
                  className="progress-fill"
                  style={{
                    width: `${usageData.creditsTotal > 0
                      ? Math.min(100, Math.max(0, (usageData.creditsUsed / usageData.creditsTotal) * 100))
                      : 0}%`,
                  }}
                  data-testid="progress-fill"
                ></div>
              </div>
              <span className="metric-value" data-testid="credits-value">
                {usageData.creditsUsed} used of {usageData.creditsTotal} purchased ·{" "}
                {usageData.creditsRemaining} remaining
              </span>
            </div>
            <div className="metric-item" data-testid="cdds-month-metric">
              <span className="metric-label">Chargeable Events This Month</span>
              <span className="metric-value" data-testid="cdds-month-value">
                {usageData.cddsThisMonth}
              </span>
            </div>
            <div className="metric-item" data-testid="cdds-ytd-metric">
              <span className="metric-label">Chargeable Events Year to Date</span>
              <span className="metric-value" data-testid="cdds-ytd-value">
                {usageData.cddsYearToDate}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Modals ── */}
      {showChangePlanModal && (
        <TopUpModal
          accountData={accountData}
          selectedPlan={selectedPlan}
          setSelectedPlan={setSelectedPlan}
          showPaymentStep={showPaymentStep}
          setShowPaymentStep={setShowPaymentStep}
          onClose={() => setShowChangePlanModal(false)}
          handlePaymentSuccess={handlePaymentSuccess}
        />
      )}

      {showPaymentModal && (
        <PaymentMethodModal
          billingData={billingData}
          onClose={() => setShowPaymentModal(false)}
          handleUpdatePayment={handleUpdatePayment}
        />
      )}

      {showInvoicesModal && (
        <InvoicesModal
          invoices={invoices}
          loading={invoicesLoading}
          onClose={() => setShowInvoicesModal(false)}
        />
      )}

      {showLedgerModal && (
        <CreditHistoryModal
          ledger={ledger}
          onClose={() => setShowLedgerModal(false)}
        />
      )}

      {showEditAccountModal && (
        <EditAccountModal
          editAccountData={editAccountData}
          setEditAccountData={setEditAccountData}
          onClose={() => setShowEditAccountModal(false)}
          handleEditAccount={handleEditAccount}
        />
      )}

      {showEditAdminModal && (
        <EditAdminModal
          editAccountData={editAccountData}
          setEditAccountData={setEditAccountData}
          onClose={() => setShowEditAdminModal(false)}
          handleEditAdmin={handleEditAdmin}
        />
      )}
    </div>
  );
};
